import React, { useState } from 'react';
import { format } from 'date-fns';
import { CalendarDays, AlertCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Calendar } from './calendar';

export interface DatePickerFieldProps {
  label?: string;
  /** Accepts any parseable date string: '', 'yyyy-MM-dd', 'MM/dd/yyyy' */
  value: string;
  /** Emits 'yyyy-MM-dd' on selection; '' on clear */
  onChange: (isoDate: string) => void;
  error?: string;
  required?: boolean;
  className?: string;
}

// ── Parse any incoming date string → JS Date ──────────────────────────────────
function parseToDate(val: string): Date | undefined {
  if (!val) return undefined;
  // MM/dd/yyyy — API/schema output format
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
    const [m, d, y] = val.split('/');
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return isNaN(date.getTime()) ? undefined : date;
  }
  // yyyy-MM-dd — ISO
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
    const [y, m, d] = val.split('-');
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return isNaN(date.getTime()) ? undefined : date;
  }
  // Fallback
  try {
    const date = new Date(val);
    return isNaN(date.getTime()) ? undefined : date;
  } catch {
    return undefined;
  }
}

const today = new Date();
const minDate = new Date(new Date().setFullYear(today.getFullYear() - 120));

/**
 * DatePickerField — label-above Popover+Calendar date picker.
 * Built on shadcn Popover + Calendar (react-day-picker).
 * Matches FloatingInput styling exactly.
 * Emits yyyy-MM-dd → existing schema transform → MM/dd/yyyy for API.
 */
const DatePickerField: React.FC<DatePickerFieldProps> = ({
  label = 'Date of Birth',
  value,
  onChange,
  error,
  required,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const dateValue = parseToDate(value);

  const handleSelect = (date: Date | undefined) => {
    onChange(date ? format(date, 'yyyy-MM-dd') : '');
    setOpen(false);
  };

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>

      {/* ── Label ── */}
      <label
        className={cn(
          'block text-[11.5px] font-medium leading-none select-none',
          error ? 'text-red-500' : 'text-slate-500 dark:text-slate-400',
        )}
      >
        {label}
        {required && <span className="text-red-400 ml-0.5">*</span>}
      </label>

      {/* ── Popover trigger ── */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Pick date of birth"
            onClick={(e) => {
              e.preventDefault();
              setOpen(true);
            }}
            className={cn(
              'flex h-[40px] w-full items-center gap-2.5 rounded-xl border px-3.5 text-left',
              'bg-white dark:bg-surface-dark-300',
              'text-[13.5px] transition-all duration-150 outline-none',
              // ── Focus / open state ──
              open
                ? 'border-brand-500 dark:border-brand-400/60 shadow-[0_0_0_3px_rgba(3,93,184,0.10)] dark:shadow-[0_0_0_3px_rgba(43,141,224,0.15)]'
                : error
                  ? 'border-red-300 dark:border-red-500/50 hover:border-red-300'
                  : 'border-slate-300 dark:border-white/[0.18] hover:border-brand-400/50 dark:hover:border-white/30',
            )}
          >
            <CalendarDays
              size={14}
              className={cn(
                'shrink-0 transition-colors pointer-events-none',
                dateValue ? 'text-brand-500 dark:text-brand-400' : 'text-slate-400 dark:text-slate-500',
              )}
            />
            {dateValue ? (
              <span className="text-slate-800 dark:text-slate-100 font-medium pointer-events-none">
                {format(dateValue, 'dd MMM yyyy')}
              </span>
            ) : (
              <span className="text-slate-400 dark:text-slate-500 font-normal pointer-events-none">
                Select date
              </span>
            )}
          </button>
        </PopoverTrigger>

        <PopoverContent align="start" className="p-0 w-auto z-[9999]">
          <Calendar
            mode="single"
            selected={dateValue}
            defaultMonth={dateValue ?? new Date(today.getFullYear() - 25, 0, 1)}
            onSelect={handleSelect}
            captionLayout="dropdown"
            fromYear={today.getFullYear() - 120}
            toYear={today.getFullYear()}
            disabled={(date) => date > today || date < minDate}
          />
        </PopoverContent>
      </Popover>

      {/* ── Error ── */}
      {error && error.trim() && (
        <div className="flex items-center gap-1 mt-0.5">
          <AlertCircle size={11} className="text-red-500 shrink-0" />
          <p className="text-[11px] text-red-500">{error}</p>
        </div>
      )}
    </div>
  );
};

export default DatePickerField;
